import { useCallback, useEffect, useRef, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiError } from "@/lib/api";
import { cropImageToAvatarBlob } from "@/lib/avatar-crop";
import { uploadAvatar } from "@/lib/me-api";
import type { AuthUser } from "@/lib/auth";

type AvatarCropDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string | null;
  onUploaded: (user: AuthUser) => void;
};

export function AvatarCropDialog({ open, onOpenChange, imageSrc, onUploaded }: AvatarCropDialogProps) {
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropSize, setCropSize] = useState<{ width: number; height: number } | undefined>();
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }, [open, imageSrc]);

  // Dialog 内容经 Portal 挂载；用 callback ref 在节点真正出现时再量尺寸
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;

    if (!node) {
      setCropSize(undefined);
      return;
    }

    const updateSize = () => {
      // clientWidth 不受 dialog zoom-in transform 影响
      const side = Math.round(Math.min(node.clientWidth, node.clientHeight));
      if (side <= 0) return;
      setCropSize((prev) => (prev?.width === side && prev?.height === side ? prev : { width: side, height: side }));
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    resizeObserverRef.current = observer;
  }, []);

  useEffect(() => () => resizeObserverRef.current?.disconnect(), []);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  async function handleConfirm() {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsUploading(true);
    try {
      const blob = await cropImageToAvatarBlob(imageSrc, croppedAreaPixels);
      const { user } = await uploadAvatar(blob);
      onUploaded(user);
      toast.success("头像已更新");
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "上传失败，请稍后重试");
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>裁剪头像</DialogTitle>
          <DialogDescription>
            圆内所见即上传后圆形头像；可拖动与缩放，导出 512×512。
          </DialogDescription>
        </DialogHeader>

        <div ref={containerRef} className="relative aspect-square w-full overflow-hidden rounded-lg bg-muted">
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={1}
              maxZoom={3}
              aspect={1}
              cropShape="round"
              showGrid={false}
              objectFit="contain"
              cropSize={cropSize}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          ) : null}
        </div>

        <div className="space-y-2">
          <label className="text-xs text-muted-foreground" htmlFor="avatar-zoom">
            缩放
          </label>
          <input
            id="avatar-zoom"
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            className="w-full accent-primary"
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={isUploading} onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button type="button" disabled={isUploading || !imageSrc} onClick={() => void handleConfirm()}>
            {isUploading ? "上传中…" : "确认上传"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
